import React from "react";
import { Link } from "react-router-dom";
import { OrBadge } from "../../atoms/or-badge";
import "./or-card.css";
import { useOrCard } from "./or-card.hook";

export type { OrCardVariant } from "./or-card.hook";

export interface OrCardBadgeItem {
  ariaLabel?: string;
  href?: string;
  id: string;
  label: string;
  onSelect?: () => void;
  to?: string;
  variant?: import("../../atoms/or-badge").OrBadgeVariant;
}

export interface OrCardTaskItem {
  href?: string;
  icon?: React.ReactNode;
  id: string;
  isCompleted?: boolean;
  label: string;
  meta: string;
  onSelect?: () => void;
  to?: string;
}

interface OrCardBaseProps {
  className?: string;
  description?: string;
  icon?: React.ReactNode;
  title: string;
}

export interface OrCardFeatureProps extends OrCardBaseProps {
  badges: readonly OrCardBadgeItem[];
  variant: "feature";
}

export interface OrCardContentProps extends OrCardBaseProps {
  children: React.ReactNode;
  variant: "content";
}

export interface OrCardShortcutProps extends OrCardBaseProps {
  href?: string;
  onSelect?: () => void;
  to?: string;
  variant: "shortcut";
}

export interface OrCardSummaryProps extends OrCardBaseProps {
  items: readonly OrCardTaskItem[];
  itemsTitle?: string;
  primaryLegendIcon?: React.ReactNode;
  primaryLabel?: string;
  primaryValue: number;
  secondaryLegendIcon?: React.ReactNode;
  secondaryLabel?: string;
  secondaryValue: number;
  summaryTitle?: string;
  total: number;
  totalLabel?: string;
  variant: "summary";
}

export type OrCardProps = OrCardContentProps | OrCardFeatureProps | OrCardShortcutProps | OrCardSummaryProps;

function ArrowIcon(): React.ReactElement {
  return (
    <svg fill="none" height="20" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20">
      <line x1="7" x2="17" y1="17" y2="7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  );
}

function CheckIcon(): React.ReactElement {
  return (
    <svg fill="none" height="12" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="12">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronIcon(): React.ReactElement {
  return (
    <svg fill="none" height="12" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" width="12">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function TaskAction({
  children,
  href,
  isCompleted = false,
  onSelect,
  to,
}: {
  children: React.ReactNode;
  href?: string;
  isCompleted?: boolean;
  onSelect?: () => void;
  to?: string;
}): React.ReactElement {
  const trailingIcon = (
    <span className={`or-card__task-check ${isCompleted ? "or-card__task-check--completed" : ""}`}>
      {isCompleted ? <CheckIcon /> : <ChevronIcon />}
    </span>
  );

  if (to) {
    return (
      <Link className="or-card__task-action" onClick={() => onSelect?.()} to={to}>
        {children}
        {trailingIcon}
      </Link>
    );
  }

  if (href) {
    return (
      <a className="or-card__task-action" href={href} onClick={() => onSelect?.()}>
        {children}
        {trailingIcon}
      </a>
    );
  }

  if (onSelect) {
    return (
      <button className="or-card__task-action" onClick={onSelect} type="button">
        {children}
        {trailingIcon}
      </button>
    );
  }

  return (
    <div className="or-card__task-action or-card__task-action--readonly">
      {children}
      {trailingIcon}
    </div>
  );
}

function SummaryCard(props: OrCardSummaryProps): React.ReactElement {
  const { cardClassName, circumference, primaryPercent, primaryStroke, secondaryStroke } = useOrCard({
    className: props.className,
    primaryValue: props.primaryValue,
    secondaryValue: props.secondaryValue,
    variant: props.variant,
  });

  const secondaryPercent = props.primaryValue + props.secondaryValue > 0 ? 100 - primaryPercent : 0;

  return (
    <article className={cardClassName}>
      <h3 className="or-card__group-title">{props.title}</h3>
      <div className="or-card__summary-layout">
        <div className="or-card__chart">
          <h4 className="or-card__chart-title">{props.summaryTitle ?? "Progreso"}</h4>
          <div className="or-card__donut">
            <svg className="or-card__donut-svg" height="160" viewBox="0 0 160 160" width="160">
              <circle className="or-card__donut-track" cx="80" cy="80" fill="none" r="65" strokeWidth="12" />
              <circle
                className="or-card__donut-secondary"
                cx="80"
                cy="80"
                fill="none"
                r="65"
                strokeDasharray={`${secondaryStroke} ${circumference}`}
                strokeDashoffset={-primaryStroke}
                strokeLinecap="round"
                strokeWidth="12"
                transform="rotate(-90 80 80)"
              />
              <circle
                className="or-card__donut-primary"
                cx="80"
                cy="80"
                fill="none"
                r="65"
                strokeDasharray={`${primaryStroke} ${circumference}`}
                strokeLinecap="round"
                strokeWidth="12"
                transform="rotate(-90 80 80)"
              />
            </svg>
            <div className="or-card__donut-inner">
              <span className="or-card__donut-value">{props.total}</span>
              <span className="or-card__donut-label">{props.totalLabel ?? "Total"}</span>
            </div>
          </div>
          <div className="or-card__legend">
            <span className="or-card__legend-dot or-card__legend-dot--primary" />
            <span className="or-card__legend-text">{Math.round(primaryPercent)}%</span>
            {props.primaryLegendIcon ? (
              <span aria-label={props.primaryLabel ?? "Pendientes"} className="or-card__legend-badge" role="img">
                {props.primaryLegendIcon}
              </span>
            ) : (
              <span className="or-card__legend-label">{props.primaryLabel ?? "Pendientes"}</span>
            )}
            <span className="or-card__legend-separator">&bull;</span>
            <span className="or-card__legend-dot or-card__legend-dot--secondary" />
            <span className="or-card__legend-text">{Math.round(secondaryPercent)}%</span>
            {props.secondaryLegendIcon ? (
              <span aria-label={props.secondaryLabel ?? "Completadas"} className="or-card__legend-badge" role="img">
                {props.secondaryLegendIcon}
              </span>
            ) : (
              <span className="or-card__legend-label">{props.secondaryLabel ?? "Completadas"}</span>
            )}
          </div>
        </div>
        <div className="or-card__task-panel">
          <div className="or-card__task-panel-header">
            <h4 className="or-card__task-panel-title">{props.itemsTitle ?? "Pendientes de gestionar"}</h4>
            <span className="or-card__task-panel-count">{props.total}</span>
          </div>
          <div className="or-card__task-list">
            {props.items.map((item) => (
              <TaskAction
                href={item.href}
                isCompleted={item.isCompleted}
                key={item.id}
                onSelect={item.onSelect}
                to={item.to}
              >
                <span className="or-card__task-icon">{item.icon}</span>
                <span className="or-card__task-content">
                  <span className="or-card__task-name">{item.label}</span>
                  <span className="or-card__task-meta">{item.meta}</span>
                </span>
              </TaskAction>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

function ContentCard(props: OrCardContentProps): React.ReactElement {
  const { cardClassName } = useOrCard({ className: props.className, variant: props.variant });

  return (
    <article aria-label={props.title} className={cardClassName}>
      {props.children}
    </article>
  );
}

function FeatureCard(props: OrCardFeatureProps): React.ReactElement {
  const { cardClassName } = useOrCard({ className: props.className, variant: props.variant });

  return (
    <article className={cardClassName}>
      <div className="or-card__header">
        <h3 className="or-card__title">
          {props.icon ? <span className="or-card__title-icon">{props.icon}</span> : null}
          <span>{props.title}</span>
        </h3>
      </div>
      {props.description ? <p className="or-card__description">{props.description}</p> : null}
      <div className="or-card__badges">
        {props.badges.map((badge) => (
          <OrBadge
            ariaLabel={badge.ariaLabel}
            href={badge.href}
            key={badge.id}
            label={badge.label}
            onClick={badge.onSelect}
            to={badge.to}
            variant={badge.variant ?? "normal"}
          />
        ))}
      </div>
    </article>
  );
}

function ShortcutCard(props: OrCardShortcutProps): React.ReactElement {
  const { cardClassName } = useOrCard({ className: props.className, variant: props.variant });

  const content = (
    <>
      <div className="or-card__header or-card__header--shortcut">
        <h3 className="or-card__title">
          {props.icon ? <span className="or-card__title-icon">{props.icon}</span> : null}
          <span>{props.title}</span>
        </h3>
        <span className="or-card__shortcut-arrow">
          <ArrowIcon />
        </span>
      </div>
      {props.description ? <p className="or-card__description">{props.description}</p> : null}
    </>
  );

  if (props.to) {
    return (
      <Link className={cardClassName} onClick={() => props.onSelect?.()} to={props.to}>
        {content}
      </Link>
    );
  }

  if (props.href) {
    return (
      <a className={cardClassName} href={props.href} onClick={() => props.onSelect?.()}>
        {content}
      </a>
    );
  }

  if (props.onSelect) {
    return (
      <button className={cardClassName} onClick={props.onSelect} type="button">
        {content}
      </button>
    );
  }

  return <article className={cardClassName}>{content}</article>;
}

export function OrCard(props: OrCardProps): React.ReactElement {
  if (props.variant === "content") {
    return <ContentCard {...props} />;
  }

  if (props.variant === "summary") {
    return <SummaryCard {...props} />;
  }

  if (props.variant === "feature") {
    return <FeatureCard {...props} />;
  }

  return <ShortcutCard {...props} />;
}
